import { useCallback } from 'react';
import { getSupabase } from '@/lib/supabaseClient';
import { useToast } from "@/components/ui/use-toast";
import { useLanguage } from '@/contexts/LanguageContext';
import { differenceInDays } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { streakService } from '@/services/streakService';


function useAuthActions(
  setLoadingAuthAction, 
  refetchProfile,
  setShowTermsDialog,
  checkTermsAgreement,
  refreshAuthData
) {
  const supabase = getSupabase();
  const { toast } = useToast();
  const { t, isReady: isLanguageReady } = useLanguage();
  const navigate = useNavigate();

  const loginUser = useCallback(async (email, password) => {
    if (!supabase || !isLanguageReady) {
      console.error("Supabase client not available or language not ready for login");
      if (isLanguageReady) {
        toast({ title: t('loginPage.errors.supabaseClient'), variant: "destructive" });
      }
      return { success: false };
    }
    setLoadingAuthAction(true);
    try {
      let previousLastLogin = null;

      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({ email, password });
      if (authError) throw authError;
      if (!authData.user) throw new Error(t('loginPage.errors.noUser'));

      if (!authData.user.email_confirmed_at) {
        await supabase.auth.signOut();
        toast({
          title: t('loginPage.errors.title'),
          description: t('loginPage.errors.emailNotConfirmed'),
          variant: 'destructive'
        });
        setLoadingAuthAction(false);
        return { success: false, reason: 'EMAIL_NOT_CONFIRMED' };
      }
      
      await refreshAuthData();

      try {
        const { data: lastLoginData, error: lastLoginError } = await supabase
          .from('profiles')
          .select('last_login_at')
          .eq('id', authData.user.id)
          .maybeSingle();
        if (!lastLoginError && lastLoginData?.last_login_at) {
          previousLastLogin = new Date(lastLoginData.last_login_at);
        }

        const { error: updateError } = await supabase
          .from('profiles')
          .update({ last_login_at: new Date().toISOString() })
          .eq('id', authData.user.id);
        if (updateError) {
          console.error('Error updating last login timestamp:', updateError);
        }
      } catch (err) {
        console.error('Error handling last login timestamp:', err);
      }

      const userProfile = await refetchProfile(authData.user.id);

      if (userProfile && userProfile.deletion_status === 'pending_deletion') {
        const deletionRequestedAt = new Date(userProfile.deletion_requested_at);
        if (differenceInDays(new Date(), deletionRequestedAt) <= 30) {
          const { error: updateError } = await supabase
            .from('profiles')
            .update({ deletion_status: 'cancelled', deletion_requested_at: null })
            .eq('id', authData.user.id);
          
          if (updateError) {
            console.error('Error cancelling account deletion:', updateError);
            toast({ title: t('common.error'), description: t('loginPage.errors.cancelDeletionError'), variant: "destructive" });
          } else {
            toast({ title: t('loginPage.welcomeBackTitle'), description: t('loginPage.welcomeBackMessage') });
            await refetchProfile(authData.user.id); 
          }
        } else {
          await supabase.auth.signOut();
          await refreshAuthData(); 
          setLoadingAuthAction(false);
          toast({ title: t('loginPage.errors.accountPastRecoveryTitle'), description: t('loginPage.errors.accountPastRecoveryMessage'), variant: "destructive" });
          return false; 
        }
      }
      
      if (typeof checkTermsAgreement === 'function') checkTermsAgreement();

      try {
        const accountAgeDays = differenceInDays(new Date(), new Date(authData.user.created_at));
        const thresholds = [30, 90, 180, 365];
        for (const days of thresholds) {
          if (accountAgeDays >= days) {
            await streakService.awardSingleUseReward(
              authData.user.id,
              'account_age',
              `${days}-day account age`
            );
          }
        }
      } catch (err) {
        console.error('Error awarding account age reward:', err);
      }

      toast({
        title: t('loginPage.successTitle', 'Login Successful!'),
        description: t('loginPage.successMessage', 'Welcome back! Redirecting you to your dashboard...')
      });

      const now = new Date();
      
      // Record this login in the login history
      try {
        await supabase.rpc('record_user_login', {
          p_user_id: authData.user.id,
          p_login_type: 'password'
        });
      } catch (error) {
        console.warn('Failed to record login history:', error);
      }
      
      // Check if this is actually the user's first login ever
      let isFirstLoginEver = false;
      try {
        // Check if there are any previous successful logins (excluding current session)
        const { data: previousLogins } = await supabase
          .from('user_login_history')
          .select('id')
          .eq('user_id', authData.user.id)
          .eq('success', true)
          .lt('login_timestamp', now.toISOString())
          .limit(1);
        
        isFirstLoginEver = !previousLogins || previousLogins.length === 0;
        console.log('First login check:', { isFirstLoginEver, previousLoginsCount: previousLogins?.length || 0 });
      } catch (error) {
        console.warn('Failed to check first login status:', error);
        // Fallback to profile creation date check
        isFirstLoginEver = !userProfile?.created_at || 
          (new Date(userProfile.created_at).getTime() > now.getTime() - (24 * 60 * 60 * 1000));
      }
      
      if (isFirstLoginEver) {
        // This is truly the first login - show first login message
        console.log('Setting first login notification flag');
        sessionStorage.setItem('showLastLoginNotification', 'true');
        sessionStorage.removeItem('lastLoginTimestamp'); // No previous timestamp
      } else {
        // Get the actual last login timestamp from history (excluding current session)
        try {
          const { data: lastLoginData } = await supabase
            .from('user_login_history')
            .select('login_timestamp')
            .eq('user_id', authData.user.id)
            .eq('success', true)
            .lt('login_timestamp', now.toISOString())
            .order('login_timestamp', { ascending: false })
            .limit(1)
            .single();
          
          if (lastLoginData) {
            const lastLogin = new Date(lastLoginData.login_timestamp);
            const timeDiff = now.getTime() - lastLogin.getTime();
            const hoursDiff = timeDiff / (1000 * 60 * 60);
            
            // Show notification for any previous login (removed time restriction)
            console.log('Last login check:', { hoursDiff, lastLogin: lastLogin.toISOString(), willShowNotification: true });
            console.log('Setting last login notification flag');
            sessionStorage.setItem('showLastLoginNotification', 'true');
            sessionStorage.setItem('lastLoginTimestamp', lastLogin.toISOString());
          }
        } catch (error) {
          console.warn('Failed to get last login timestamp:', error);
          // Fallback to previous logic - show notification for any previous login
          console.log('Using fallback logic - setting notification flag');
          sessionStorage.setItem('showLastLoginNotification', 'true');
          if (previousLastLogin) {
            sessionStorage.setItem('lastLoginTimestamp', previousLastLogin.toISOString());
          }
        }
      }

      // Track daily login streak using simplified streak service
      try {
        const { simplifiedStreakService } = await import('@/services/simplifiedStreakService');
        const result = await simplifiedStreakService.trackDailyLogin(authData.user.id);
        
        if (process.env.NODE_ENV === 'development') {
          console.log(`Daily login streak updated: ${result?.current_streak || 0} days`);
        }
        
        // Send notification for login streak milestones
        if (result?.current_streak === 2) {
          toast({
            title: '🔥 Login Streak: 2 Days!',
            description: 'Great job logging in consistently! Keep it up!',
            variant: 'default'
          });
        } else if (result?.current_streak === 7) {
          toast({
            title: '🎉 Week Warrior!',
            description: 'Amazing! 7 days of consistent logins!',
            variant: 'default'
          });
        } else if (result?.current_streak === 30) {
          toast({
            title: '🏆 Monthly Master!',
            description: 'Incredible! 30 days of daily logins!',
            variant: 'default'
          });
        } else if (result?.current_streak > 2 && result?.current_streak % 5 === 0) {
          toast({
            title: `🔥 ${result?.current_streak} Day Login Streak!`,
            description: `You're on fire! ${result?.current_streak} days of consistent logins!`,
            variant: 'default'
          });
        }
      } catch (error) {
        console.warn('Failed to track daily login streak:', error);
      }

      // Force a page refresh after login
      window.location.reload();
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      toast({
        title: t('loginPage.errors.title'),
        description: error.message || t('loginPage.errors.generic'),
        variant: "destructive",
      });
      return { success: false };
    } finally {
      setLoadingAuthAction(false);
    }
  }, [supabase, setLoadingAuthAction, refetchProfile, toast, t, isLanguageReady, navigate, checkTermsAgreement, refreshAuthData]);

  const registerUser = useCallback(async (email, password, firstName = '', lastName = '', extra = {}) => {
    if (!supabase || !isLanguageReady) {
      console.error("Supabase client not available or language not ready for registration");
      if (isLanguageReady) {
        toast({ title: t('registerPage.errors.supabaseClient'), variant: "destructive" });
      }
      return false;
    }
    setLoadingAuthAction(true);
    try {
      const confirmRedirect =
        import.meta.env.VITE_CONFIRM_REDIRECT_URL ||
        `${window.location.origin}/email-confirmed`;
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: confirmRedirect,
          data: {
            first_name: firstName,
            last_name: lastName,
            gender: extra.gender || null,
            location: extra.location || null,
            preferred_language: extra.preferredLanguage || null,
          }
        }
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error(t('registerPage.errors.noUser'));

      await refreshAuthData();

      try {
        await streakService.awardSingleUseReward(
          authData.user.id,
          'registration',
          'Registration Bonus'
        );
      } catch (err) {
        console.error('Error awarding registration reward:', err);
      }

      if (typeof checkTermsAgreement === 'function') checkTermsAgreement();

      // Update profile with additional fields
      try {
        await supabase
          .from('profiles')
          .update({
            first_name: firstName,
            last_name: lastName,
            gender: extra.gender || null,
            location: extra.location || null,
            preferred_language: extra.preferredLanguage || null,
          })
          .eq('id', authData.user.id);
      } catch (e) {
        console.warn('[registerUser] profile extra fields update failed:', e?.message || e);
      }

      return true;
    } catch (error) {
      console.error('Registration error:', error);
      toast({
        title: t('registerPage.errors.title'),
        description: error.message || t('registerPage.errors.generic'),
        variant: "destructive",
      });
      return false;
    } finally {
      setLoadingAuthAction(false);
    }
  }, [supabase, setLoadingAuthAction, toast, t, isLanguageReady, checkTermsAgreement, refreshAuthData]);

  const logoutUser = useCallback(async () => {
    if (!supabase) {
      console.error("Supabase client not available for logout");
      return false;
    }
    setLoadingAuthAction(true);
    try {
      // Attempt sign out, but don't hard-fail if network hiccups
      const { error } = await supabase.auth.signOut();
      if (error && process.env.NODE_ENV === 'development') {
        console.warn('signOut returned error; proceeding to clear local session:', error.message);
      }

      // Proactively clear auth cookies/storage regardless of signOut result
      try {
        // Our configured storageKey
        document.cookie = `urban-mint-auth=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
        // Supabase default key pattern (in case it was ever used)
        document.cookie = `sb-zmdktjragxwhmkkjdgcb-auth-token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
      } catch {}

      // Refresh local auth state from Supabase (should be null now)
      await refreshAuthData();

      toast({ 
        title: t('logoutPage.successTitle', 'Logout Successful!'),
        description: t('logoutPage.successMessage', 'You have been logged out successfully.')
      });

      // Redirect to login without forcing reload to avoid loops
      navigate('/login', { replace: true });
      return true;
    } catch (error) {
      console.error("Error during logout:", error);
      toast({ title: t('common.error'), description: t('logoutPage.errors.logoutError'), variant: "destructive" });
      return false;
    } finally {
      setLoadingAuthAction(false);
    }
  }, [supabase, refreshAuthData, toast, t, navigate]);
  
  const requestPasswordReset = useCallback(async (email) => {
    if (!supabase || !isLanguageReady) {
      if(isLanguageReady) toast({ title: t('forgotPassword.errors.supabaseClient'), variant: "destructive" });
      return false;
    }
    setLoadingAuthAction(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/update-password`, 
      });
      if (error) throw error;
      toast({ title: t('forgotPassword.success.title'), description: t('forgotPassword.success.description') });
      setLoadingAuthAction(false);
      return true;
    } catch (error) {
      toast({
        title: t('forgotPassword.errors.title'),
        description: error.message || t('forgotPassword.errors.generic'),
        variant: "destructive",
      });
      setLoadingAuthAction(false);
      return false;
    }
  }, [supabase, setLoadingAuthAction, toast, t, isLanguageReady]);

  const updateUserPassword = useCallback(async (newPassword) => {
    if (!supabase || !isLanguageReady) {
       if(isLanguageReady) toast({ title: t('updatePassword.errors.supabaseClient'), variant: "destructive" });
      return false;
    }
    setLoadingAuthAction(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      toast({ title: t('updatePassword.success.title'), description: t('updatePassword.success.description') });
      setLoadingAuthAction(false);
      navigate('/dashboard'); 
      return true;
    } catch (error) {
      toast({
        title: t('updatePassword.errors.title'),
        description: error.message || t('updatePassword.errors.generic'),
        variant: "destructive",
      });
      setLoadingAuthAction(false);
      return false;
    }
  }, [supabase, setLoadingAuthAction, toast, t, isLanguageReady, navigate]);


  const resendConfirmationEmail = useCallback(async (email) => {
    if (!supabase || !isLanguageReady) {
      if (isLanguageReady) toast({ title: t('common.error'), description: "Supabase client not available.", variant: "destructive" });
      return false;
    }
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
      });
      if (error) throw error;
      toast({ title: t('emailConfirmation.emailResentTitle'), description: t('emailConfirmation.emailResentMessage') });
      return true;
    } catch (error) {
      toast({ title: t('common.error'), description: error.message || "Failed to resend confirmation email.", variant: "destructive" });
      return false;
    }
  }, [supabase, toast, t, isLanguageReady]);

  const requestAccountDeletion = useCallback(async (userId) => {
    if (!supabase || !userId || !isLanguageReady) {
      if (isLanguageReady) toast({ title: t('common.error'), description: "User ID or Supabase client missing.", variant: "destructive" });
      return false;
    }
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ 
          deletion_requested_at: new Date().toISOString(),
          deletion_status: 'pending_deletion'
        })
        .eq('id', userId);
      if (error) throw error;
      toast({ title: t('profilePage.deleteAccount.requestSuccessTitle'), description: t('profilePage.deleteAccount.requestSuccessMessage') });
      
      await refetchProfile(userId); 
      return true;
    } catch (error) {
      toast({ title: t('common.error'), description: error.message || "Failed to request account deletion.", variant: "destructive" });
      return false;
    }
  }, [supabase, toast, t, isLanguageReady, refetchProfile]);


  return { 
    loginUser, 
    registerUser, 
    logoutUser, 
    requestPasswordReset, 
    updateUserPassword, 
    resendConfirmationEmail, 
    requestAccountDeletion,
  };
}

export { useAuthActions };
  
